import React from 'react';
import { Dropdown } from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  CrownOutlined,
  StopOutlined,
  LoadingOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { PermissionLevel } from '../../pages/Admin/components/hooks/useRolePermissionsPage';

interface PermissionToggleChipProps {
  level: PermissionLevel;
  onSelect: (level: PermissionLevel) => void;
  disabled?: boolean;
  loading?: boolean;
}

const LEVELS: PermissionLevel[] = ['none', 'read', 'write', 'manage'];

const LEVEL_CONFIG: Record<PermissionLevel, {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: React.ReactNode;
  description: string;
}> = {
  none: {
    label: 'Нет доступа',
    color: 'var(--color-text-disabled)',
    bg: 'var(--color-bg-primary)',
    border: 'var(--color-border-primary)',
    icon: <StopOutlined />,
    description: 'Ресурс недоступен',
  },
  read: {
    label: 'Чтение',
    color: 'var(--color-info)',
    bg: 'var(--color-bg-primary)',
    border: 'var(--color-info)',
    icon: <EyeOutlined />,
    description: 'Просмотр данных',
  },
  write: {
    label: 'Редакт.',
    color: 'var(--color-warning)',
    bg: 'var(--color-bg-primary)',
    border: 'var(--color-warning)',
    icon: <EditOutlined />,
    description: 'Просмотр и изменение',
  },
  manage: {
    label: 'Полное',
    color: 'var(--color-accent)',
    bg: 'var(--color-bg-primary)',
    border: 'var(--color-accent)',
    icon: <CrownOutlined />,
    description: 'Все операции',
  },
};

export const PermissionToggleChip: React.FC<PermissionToggleChipProps> = ({
  level,
  onSelect,
  disabled = false,
  loading = false,
}) => {
  const config = LEVEL_CONFIG[level];

  const menuItems = LEVELS.map((lvl) => {
    const cfg = LEVEL_CONFIG[lvl];
    const isActive = lvl === level;
    return {
      key: lvl,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
          <span style={{ color: cfg.color, fontSize: 13, display: 'flex' }}>{cfg.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: isActive ? 700 : 500, fontSize: 12.5, color: cfg.color }}>
              {cfg.label}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--color-text-disabled)', lineHeight: 1.2, marginTop: 1 }}>
              {cfg.description}
            </div>
          </div>
          {isActive && (
            <span style={{ color: cfg.color, fontSize: 14, fontWeight: 700, lineHeight: 1 }}>✓</span>
          )}
        </div>
      ),
      style: {
        background: isActive ? `${cfg.color}08` : undefined,
        borderRadius: 6,
      } as React.CSSProperties,
    };
  });

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: ({ key }) => {
          if (key !== level) onSelect(key as PermissionLevel);
        },
        style: { padding: 4, minWidth: 200 },
      }}
      trigger={['click']}
      disabled={disabled || loading}
      placement="bottomRight"
    >
      <button
        type="button"
        disabled={disabled || loading}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          minWidth: 105,
          height: 30,
          borderRadius: 8,
          border: `1.5px solid ${config.border}`,
          background: config.bg,
          color: config.color,
          fontSize: 12,
          fontWeight: 600,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          outline: 'none',
          padding: '0 8px 0 10px',
          letterSpacing: 0.2,
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 12, lineHeight: 1, display: 'flex' }}>
          {loading ? <LoadingOutlined spin /> : config.icon}
        </span>
        <span style={{ flex: 1, textAlign: 'left' }}>{config.label}</span>
        {!loading && (
          <DownOutlined style={{ fontSize: 9, opacity: 0.5, marginLeft: 2 }} />
        )}
      </button>
    </Dropdown>
  );
};
